import React from 'react';

export const FONT = 'system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif';
export const C = {
  teal: '#314F5C', tealMid: '#5a8fa6', tealLight: '#AFE1F6', tealPale: '#E0F5FC',
  dark: '#0c1c28', text: '#0f1e28', textMid: '#4a6878', textMuted: '#8aacbc',
  border: '#e2ebf0', bg: '#f5f7f9', white: '#fff',
  green: '#17a86a', greenBg: '#eaf7f2', greenText: '#0d6640',
  red: '#e53935',   redBg:   '#fef0ef', redText:   '#8b1c1c',
  amber: '#d97706', amberBg: '#fef3e2', amberText: '#7c4a0a',
  blue:  '#2563eb', blueBg:  '#eff4ff', blueText:  '#1e3a8a',
};

/* ── Metric card ─────────────────────────────────────────────────────────── */
export function MetricCard({ label, value, sub, trend, accent, style }) {
  const trendColor = trend === 'up' ? C.green : trend === 'down' ? C.red : C.textMuted;
  const accentBar = accent || (trend === 'up' ? C.green : trend === 'down' ? C.red : null);
  return (
    <div style={{
      background: C.white, borderRadius: 12, padding: '18px 20px',
      boxShadow: '0 1px 3px rgba(15,30,40,0.07), 0 0 0 1px rgba(15,30,40,0.04)',
      position: 'relative', overflow: 'hidden',
      transition: 'box-shadow .18s ease, transform .18s ease',
      ...style
    }}>
      {accentBar && (
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, height: 3,
          background: accentBar, borderRadius: '12px 12px 0 0',
        }} />
      )}
      <div style={{ fontSize: '1.9rem', fontWeight: 400, color: C.text, lineHeight: 1, letterSpacing: '-.02em', marginBottom: 6 }}>
        {value}
      </div>
      <div style={{ fontSize: '.75rem', color: C.textMid, fontWeight: 500, letterSpacing: '.01em', marginBottom: sub ? 4 : 0 }}>
        {label}
      </div>
      {sub && (
        <div style={{ fontSize: '.72rem', color: trendColor, display: 'flex', alignItems: 'center', gap: 3 }}>
          {trend === 'up'   && <span>↑</span>}
          {trend === 'down' && <span>↓</span>}
          {sub}
        </div>
      )}
    </div>
  );
}

/* ── Card ──────────────────────────────────────────────────────────────────  */
export function Card({ children, style }) {
  return (
    <div style={{
      background: C.white, borderRadius: 12, padding: '20px 22px', marginBottom: 14,
      boxShadow: '0 1px 3px rgba(15,30,40,0.07), 0 0 0 1px rgba(15,30,40,0.04)',
      ...style
    }}>
      {children}
    </div>
  );
}

/* ── CardTitle ──────────────────────────────────────────────────────────── */
export function CardTitle({ children, action }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
      <div style={{ fontSize: '.78rem', color: C.textMid, fontWeight: 600, letterSpacing: '.01em' }}>
        {children}
      </div>
      {action}
    </div>
  );
}

/* ── BarRow ─────────────────────────────────────────────────────────────── */
export function BarRow({ label, value, pct, color, labelWidth = 88 }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
      <span style={{ fontSize: '.82rem', color: C.text, flexShrink: 0, width: labelWidth }}>{label}</span>
      <div style={{ flex: 1, height: 7, background: '#eae9e6', borderRadius: 999, overflow: 'hidden' }}>
        <div style={{
          width: `${Math.min(Math.max(pct, 0), 100)}%`, height: '100%',
          borderRadius: 999, background: color || C.tealMid,
          transition: 'width .5s cubic-bezier(0.4,0,0.2,1)',
        }} />
      </div>
      <span style={{
        fontSize: '.8rem', color: color || C.textMuted,
        width: 52, textAlign: 'right', flexShrink: 0,
        fontVariantNumeric: 'tabular-nums', fontWeight: 500,
      }}>{value}</span>
    </div>
  );
}

/* ── FunnelRow ──────────────────────────────────────────────────────────── */
export function FunnelRow({ label, count, pct, width }) {
  return (
    <div style={{ marginBottom: 7 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px', background: '#eae9e6', borderRadius: 7, marginBottom: 3 }}>
        <span style={{ fontSize: '.82rem', color: C.text }}>{label}</span>
        <span>
          <span style={{ fontSize: '.84rem', fontWeight: 600, color: C.text, fontVariantNumeric: 'tabular-nums' }}>{count.toLocaleString()}</span>
          <span style={{ fontSize: '.74rem', color: C.textMuted, marginLeft: 6 }}>{pct}</span>
        </span>
      </div>
      <div style={{ height: 3, background: '#dde8ef', borderRadius: 999, overflow: 'hidden' }}>
        <div style={{ width: `${width}%`, height: '100%', background: C.teal, borderRadius: 999, transition: 'width .5s' }} />
      </div>
    </div>
  );
}

/* ── Pill ───────────────────────────────────────────────────────────────── */
export function Pill({ children, type = 'blue' }) {
  const s = {
    green:  { background: C.greenBg, color: C.greenText },
    red:    { background: C.redBg,   color: C.redText },
    blue:   { background: C.blueBg,  color: C.blueText },
    amber:  { background: C.amberBg, color: C.amberText },
    teal:   { background: '#e4eef2', color: C.teal },
    dark:   { background: '#e4eef2', color: C.text },
  }[type] || { background: C.blueBg, color: C.blueText };
  return (
    <span style={{ fontSize: '.73rem', fontWeight: 600, borderRadius: 6, padding: '2px 8px', display: 'inline-block', ...s }}>
      {children}
    </span>
  );
}

/* ── Insight ────────────────────────────────────────────────────────────── */
export function Insight({ children, type = 'info' }) {
  const s = {
    info: { bg: '#f0f7fc', border: '#b6d4e8', color: '#1a4a62', icon: 'ℹ' },
    warn: { bg: C.amberBg,  border: '#f5c86a', color: C.amberText, icon: '⚠' },
    ok:   { bg: C.greenBg,  border: '#7dcfaa', color: C.greenText, icon: '✓' },
  }[type] || { bg: '#f0f7fc', border: '#b6d4e8', color: '#1a4a62', icon: 'ℹ' };
  return (
    <div style={{
      background: s.bg, border: `1px solid ${s.border}`,
      borderRadius: 8, padding: '10px 13px',
      fontSize: '.8rem', color: s.color, lineHeight: 1.6, marginTop: 12,
      display: 'flex', gap: 8, alignItems: 'flex-start',
    }}>
      <span style={{ flexShrink: 0, fontSize: '.85rem' }}>{s.icon}</span>
      <span>{children}</span>
    </div>
  );
}

/* ── Btn ────────────────────────────────────────────────────────────────── */
export function Btn({ children, onClick, variant = 'primary', small, icon, disabled }) {
  const variants = {
    primary: { background: C.teal, color: '#fff', border: 'none', hover: '#253e4a' },
    outline:  { background: 'transparent', color: C.teal, border: `1px solid ${C.border}`, hover: '#f0f4f7' },
    green:    { background: C.green, color: '#fff', border: 'none', hover: '#12905a' },
    danger:   { background: C.redBg, color: C.redText, border: 'none', hover: '#fde0de' },
  };
  const v = variants[variant] || variants.primary;
  return (
    <button onClick={disabled ? undefined : onClick} disabled={!!disabled} style={{
      ...v, borderRadius: 7, display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: small ? '4px 10px' : '7px 14px',
      fontSize: small ? '.75rem' : '.8rem',
      cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.45 : 1, fontFamily: FONT, fontWeight: 600,
    }}>
      {icon && <span style={{ display: 'flex' }}>{icon}</span>}
      {children}
    </button>
  );
}

/* ── Input / Select ─────────────────────────────────────────────────────── */
export function Input({ value, onChange, placeholder, style }) {
  return (
    <input value={value} onChange={onChange} placeholder={placeholder} style={{
      fontSize: '.85rem', border: `1px solid ${C.border}`, borderRadius: 8,
      padding: '8px 11px', color: C.text, outline: 'none',
      fontFamily: FONT, width: '100%', background: C.white, ...style,
    }} />
  );
}
export function Select({ value, onChange, children, style }) {
  return (
    <select value={value} onChange={onChange} style={{
      fontSize: '.82rem', border: `1px solid ${C.border}`, borderRadius: 8,
      padding: '7px 11px', color: C.text, outline: 'none',
      fontFamily: FONT, background: C.white, width: '100%', cursor: 'pointer', ...style,
    }}>
      {children}
    </select>
  );
}

/* ── Grid ───────────────────────────────────────────────────────────────── */
export function Grid({ cols, gap = 14, mb = 14, children, style }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, ${cols >= 4 ? 200 : 220}px), 1fr))`, gap, marginBottom: mb, ...style }}>
      {children}
    </div>
  );
}

/* ── LineChart ──────────────────────────────────────────────────────────── */
export function LineChart({ points, prevPoints, height = 68, labels }) {
  if (!points || points.length < 2) return null;
  const W = 540, H = height, PAD = 6;
  const max = Math.max(...points, ...(prevPoints || [0]), 1);
  const toPath = pts => pts.map((p, i) => `${(i / (pts.length - 1)) * W},${H - PAD - ((p / max) * (H - PAD * 2))}`).join(' ');
  const main = toPath(points);
  const prev = prevPoints ? toPath(prevPoints) : null;
  const lastX = W, lastY = H - PAD - ((points[points.length-1] / max) * (H - PAD * 2));
  return (
    <svg viewBox={`0 0 ${W} ${H + 16}`} style={{ width: '100%', height: H + 6 }}>
      <defs>
        <linearGradient id="lg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#314F5C" stopOpacity="0.15"/>
          <stop offset="100%" stopColor="#314F5C" stopOpacity="0"/>
        </linearGradient>
      </defs>
      {prev && <polyline points={prev} style={{ fill: 'none', stroke: '#AFE1F6', strokeWidth: 1.5, strokeDasharray: '5,3' }} />}
      <polyline points={`${main} ${W},${H} 0,${H}`} style={{ fill: 'url(#lg)' }} />
      <polyline points={main} style={{ fill: 'none', stroke: '#314F5C', strokeWidth: 2 }} />
      <circle cx={lastX} cy={lastY} r="4.5" fill="#314F5C" />
      <circle cx={lastX} cy={lastY} r="8" fill="rgba(49,79,92,0.12)" />
      {labels && labels.map((l, i) => (
        <text key={i} x={(i / (labels.length - 1)) * W} y={H + 14}
          style={{ fontSize: '10px', fill: '#8aacbc', fontFamily: FONT }}>
          {l}
        </text>
      ))}
    </svg>
  );
}

/* ── StatTile ───────────────────────────────────────────────────────────── */
export function StatTile({ value, label, color = '#17a86a' }) {
  return (
    <div style={{ background: `${color}12`, borderRadius: 10, padding: '14px 16px', textAlign: 'center' }}>
      <div style={{ fontSize: '1.65rem', fontWeight: 400, color, lineHeight: 1, letterSpacing: '-.02em' }}>{value}</div>
      <div style={{ fontSize: '.72rem', color: C.textMid, marginTop: 5, fontWeight: 500 }}>{label}</div>
    </div>
  );
}

/* ── Divider ────────────────────────────────────────────────────────────── */
export function Divider({ style }) {
  return <div style={{ height: 1, background: C.border, margin: '12px 0', ...style }} />;
}

/* ── EmptyState ─────────────────────────────────────────────────────────── */

/* ── Skeleton ─────────────────────────────────────────────────────────────── */
export function Skeleton({ h = 12, mb = 10, w = '100%', radius = 6 }) {
  return (
    <div style={{
      height: h, width: w, borderRadius: radius, marginBottom: mb,
      background: 'linear-gradient(90deg,#edf2f5 25%,#f5f7f9 50%,#edf2f5 75%)',
      backgroundSize: '200% 100%',
      animation: 'shimmer 1.6s ease infinite',
    }} />
  );
}

export function EmptyState({ title, body, icon = '📭' }) {
  return (
    <div style={{ textAlign: 'center', padding: '32px 16px' }}>
      <div style={{ fontSize: '2rem', marginBottom: 10 }}>{icon}</div>
      <div style={{ fontSize: '.9rem', fontWeight: 600, color: C.text, marginBottom: 5 }}>{title}</div>
      {body && <div style={{ fontSize: '.8rem', color: C.textMuted, lineHeight: 1.6 }}>{body}</div>}
    </div>
  );
}
